import { POSITION_COORDS, spaceOut } from '../lib/formation';

const BAND = 8; // positions within this many % of each other share a row
const TOP = 12;
const BOTTOM = 88;

/**
 * Lays players out on a pitch by their assigned position.
 *
 * Sideways: players sharing a position fan out so they never overlap.
 *
 * Lengthways: the rows actually in use are spread evenly down the pitch rather
 * than pinned to their eleven-a-side coordinates. A six-a-side team is
 * GK / CB / CB / CDM / CM / ST, and at full-size coordinates that leaves the
 * whole attacking third empty with everyone bunched at the back. Spacing by row
 * fills the pitch at any squad size, and at a genuine eleven it lands within a
 * couple of percent of the original positions anyway.
 */
function layout(players) {
  const byPosition = {};
  players.forEach((p) => {
    const pos = p.assignedPosition || 'CM';
    (byPosition[pos] ||= []).push(p);
  });

  // Group the positions in use into rows, deepest first.
  const positions = Object.keys(byPosition).sort(
    (a, b) => (POSITION_COORDS[b]?.y ?? 50) - (POSITION_COORDS[a]?.y ?? 50)
  );
  const rows = [];
  positions.forEach((pos) => {
    const y = POSITION_COORDS[pos]?.y ?? 50;
    const last = rows[rows.length - 1];
    if (last && Math.abs((POSITION_COORDS[last[0]]?.y ?? 50) - y) <= BAND) last.push(pos);
    else rows.push([pos]);
  });

  // With no keeper on the pitch, hold the back line off the goal line — a defence
  // standing in its own six-yard box reads as a mistake rather than a formation.
  const hasKeeper = positions.includes('GK');
  const bottom = hasKeeper ? BOTTOM : BOTTOM - 8;

  const rowY =
    rows.length === 1
      ? () => 50
      : (i) => bottom - (i * (bottom - TOP)) / (rows.length - 1);

  const tokens = [];
  rows.forEach((row, rowIndex) => {
    const y = rowY(rowIndex);
    // Everyone on this row shares the vertical band, so fan them all out
    // together rather than per-position — otherwise two CBs and a LB collide.
    const rowPlayers = row
      .flatMap((pos) => byPosition[pos].map((p) => ({ p, pos })))
      .sort((a, b) => (POSITION_COORDS[a.pos]?.x ?? 50) - (POSITION_COORDS[b.pos]?.x ?? 50));

    // Start from each player's real x — a right winger belongs on the right,
    // even when nobody else is in that row — then separate only where tokens
    // would actually collide. Fanning everyone around the centre instead (the
    // previous approach) threw away the side a manual position edit was for.
    const xs = spaceOut(rowPlayers.map(({ pos }) => POSITION_COORDS[pos]?.x ?? 50));
    rowPlayers.forEach(({ p }, i) => tokens.push({ ...p, x: xs[i], y }));
  });
  return tokens;
}

export default function Pitch({ players, variant = 'a', onPlayerClick }) {
  const tokens = layout(players);
  const interactive = typeof onPlayerClick === 'function';

  return (
    <div className={`pitch pitch--${variant}`}>
      <div className="pitch__line pitch__line--half" />
      <div className="pitch__circle" />
      <div className="pitch__spot" />
      <div className="pitch__box pitch__box--top" />
      <div className="pitch__box pitch__box--bottom" />
      <div className="pitch__six pitch__six--top" />
      <div className="pitch__six pitch__six--bottom" />

      {tokens.map((p) => {
        const Tag = interactive ? 'button' : 'div';
        return (
          <Tag
            key={p.id}
            type={interactive ? 'button' : undefined}
            className={`token ${interactive ? 'token--interactive' : ''}`}
            style={{ left: `${p.x}%`, top: `${p.y}%` }}
            onClick={interactive ? () => onPlayerClick(p) : undefined}
            title={interactive ? `Move ${p.name} to a different position` : p.name}
          >
            <span className={`token__circle ${p._tone ? `token__circle--${p._tone}` : ''}`}>
              <span className="token__rating">{p._rating != null ? p._rating.toFixed(1) : '–'}</span>
              {p._star && <span className="token__star">★</span>}
              {p._form && <span className={`token__form token__form--${p._form}`} aria-hidden="true" />}
            </span>
            <span className="token__name">{p.name}</span>
            <span className="token__pos">
              {p._badges || p.assignedPosition}
              {p.isManualOverride ? ' ✎' : ''}
            </span>
          </Tag>
        );
      })}
    </div>
  );
}
